import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Download, Globe, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/foreign/investigations`;
const STATUS = { ricevuta: 'Ricevuta', in_corso: 'In lavorazione', completata: 'Completata', annullata: 'Annullata' };
const btn = 'h-8 px-3 text-xs flex items-center gap-1 border border-[#1F242C] text-[#BFBFBF] hover:text-white rounded-sm';

function Row({ r, headers, reload }) {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const closed = ['completata', 'annullata'].includes(r.status);
  const setStatus = async (status) => {
    const note = status === 'annullata' ? window.prompt('Motivo dell\'annullamento (visibile al cliente)') : null;
    if (status === 'annullata' && note === null) return;
    try { await axios.put(`${API}/${r.id}`, { status, note }, { headers }); toast.success('Stato aggiornato'); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    setBusy(true);
    try { await axios.post(`${API}/${r.id}/pdf`, fd, { headers }); toast.success('PDF caricato: cliente avvisato'); reload(); } catch (err) { toast.error(err?.response?.data?.detail || 'Caricamento non riuscito'); } finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const res = await axios.get(`${API}/${r.id}/pdf`, { headers, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = `Investigazione_${r.company_name}.pdf`; a.click();
    } catch { toast.error('Download non riuscito'); }
  };
  return (
    <tr className="border-b border-[#1F242C] align-top" data-testid={`admin-foreign-row-${r.id}`}>
      <td className="py-3 pr-4"><p className="text-white">{r.company_name}</p><p className="text-xs text-[#7A7F87]">{r.country}{r.reg_no ? ` · ${r.reg_no}` : ''}{r.address ? ` · ${r.address}` : ''}</p>{r.notes && <p className="text-xs text-[#8A8F97] mt-1 max-w-md whitespace-pre-line">{r.notes}</p>}</td>
      <td className="py-3 pr-4 text-xs text-[#BFBFBF]">{r.client}<br /><span className="text-[#5A5F67]">{r.requested_by}</span><br /><span className="text-[#5A5F67]">{new Date(r.created_at).toLocaleString('it-IT')}</span></td>
      <td className="py-3 pr-4 text-xs"><span className="text-[#D4AF37]" data-testid={`admin-foreign-status-${r.id}`}>{STATUS[r.status]}</span>{r.charged_cents ? <p className="text-[#5A5F67] font-mono">{(r.charged_cents / 100).toFixed(2)} €{r.refunded ? ' · rimborsata' : ''}</p> : null}</td>
      <td className="py-3">
        <div className="flex flex-wrap gap-2 justify-end">
          {r.status === 'ricevuta' && <button onClick={() => setStatus('in_corso')} className={btn} data-testid={`admin-foreign-start-${r.id}`}>In lavorazione</button>}
          {r.status !== 'annullata' && <><input ref={file} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid={`admin-foreign-file-${r.id}`} />
            <button onClick={() => file.current?.click()} disabled={busy} className={btn} data-testid={`admin-foreign-upload-${r.id}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}{busy ? 'Caricamento…' : (r.has_pdf ? 'Sostituisci PDF' : 'Carica PDF')}</button></>}
          {r.has_pdf && <button onClick={download} className={btn} data-testid={`admin-foreign-download-${r.id}`}><Download size={12} />PDF</button>}
          {!closed && <button onClick={() => setStatus('annullata')} className={`${btn} hover:text-[#E37A72]`} data-testid={`admin-foreign-cancel-${r.id}`}>Annulla e rimborsa</button>}
        </div>
      </td>
    </tr>
  );
}

export default function AdminForeignPanel({ headers }) {
  const [rows, setRows] = useState([]);
  const load = () => axios.get(API, { headers }).then((r) => setRows(r.data)).catch(() => toast.error('Investigazioni non caricate'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="bg-[#0B0D10] border border-[#1F242C] p-6" data-testid="admin-foreign-panel">
      <h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF] mb-1 flex items-center gap-2"><Globe size={15} />Investigazioni estere</h3>
      <p className="text-xs text-[#5A5F67] mb-5">Caricando il PDF la richiesta diventa «Completata» e il cliente riceve un'email. Annullando, l'importo torna sul plafond. Il prezzo unico si imposta nel listino («Investigazione azienda estera»).</p>
      {rows.length === 0 ? <p className="text-sm text-[#5A5F67]" data-testid="admin-foreign-empty">Nessuna richiesta.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><tbody>{rows.map((r) => <Row key={r.id} r={r} headers={headers} reload={load} />)}</tbody></table></div>
      )}
    </div>
  );
}
